"use client";

import { TABLE, TD, TH } from "@/components/tool-kit/styles";
import type { ChaseHit } from "@/lib/tool-kit/research-discovery/citation-chaser";

/*
 * The Citation Chaser's result table: one row per work, with the number of seed articles
 * it is linked to and its OpenAlex citation count. Every word arrives through `labels`.
 */

export interface ChaseTableLabels {
  work: string;
  seeds: string;
  citedBy: string;
  etAl: string;
  untitled: string;
  noDoi: string;
  caption: string;
}

const LINK = "break-all text-primary underline-offset-4 hover:underline";

function byline(hit: ChaseHit, etAl: string): string {
  const names = hit.authors.join(", ");
  return hit.authorCount > hit.authors.length ? `${names} ${etAl}` : names;
}

function Row({
  hit,
  labels,
  showSeeds,
  fmt,
}: {
  hit: ChaseHit;
  labels: ChaseTableLabels;
  showSeeds: boolean;
  fmt: (value: number) => string;
}) {
  const href = hit.doi
    ? `https://doi.org/${hit.doi}`
    : `https://openalex.org/${hit.id}`;
  const detail = [byline(hit, labels.etAl), hit.year, hit.venue]
    .filter((part) => part !== "" && part !== null)
    .join(" · ");
  return (
    <tr>
      <td className={`${TD} min-w-64`}>
        <a
          className={LINK}
          href={href}
          rel="noopener noreferrer"
          target="_blank"
        >
          {hit.title || labels.untitled}
        </a>
        <p className="mt-1 text-muted-foreground text-xs">{detail}</p>
        <p className="font-mono text-muted-foreground text-xs">
          {hit.doi || labels.noDoi}
        </p>
      </td>
      {showSeeds ? <td className={TD}>{fmt(hit.links)}</td> : null}
      <td className={TD}>{fmt(hit.citedBy)}</td>
    </tr>
  );
}

export function ChaseTable({
  hits,
  labels,
  showSeeds,
  fmt,
}: {
  hits: readonly ChaseHit[];
  labels: ChaseTableLabels;
  showSeeds: boolean;
  fmt: (value: number) => string;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <caption className="sr-only">{labels.caption}</caption>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {labels.work}
            </th>
            {showSeeds ? (
              <th className={TH} scope="col">
                {labels.seeds}
              </th>
            ) : null}
            <th className={TH} scope="col">
              {labels.citedBy}
            </th>
          </tr>
        </thead>
        <tbody>
          {hits.map((hit) => (
            <Row
              fmt={fmt}
              hit={hit}
              key={hit.id}
              labels={labels}
              showSeeds={showSeeds}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
